import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { formatRaidHpEok } from '../../../shared/domain/raidHpFormat.js';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { findNikkeInCatalog } from '../../nikke-catalog/domain/nikkeCatalog.js';
import { useSharedNikkeCatalog } from '../../raid-practice/shared/RaidPracticeDeckPicker.jsx';
import { buildStrategySimulationInput } from '../domain/raidStrategyRecommendation.js';
import {
  applyAllocationToPlans,
  buildMemberPlacementComparison,
  compareAllocations,
  unionCommittedScore,
  unionPlacementScore,
  unionSimulationScore,
} from '../domain/raidStrategyPlacement.js';
import { RaidTetrisMiniDeck } from './RaidTetrisMiniDeck.jsx';

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function formatEok(value) {
  return `${numberValue(value).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

function signedEok(value) {
  if (Math.abs(numberValue(value)) < 0.000001) return '0억';
  return `${value > 0 ? '+' : '-'}${formatEok(Math.abs(value))}`;
}

// 을/를 depends on whether the name ends in a consonant, and Nikke names do both.
function objectParticle(name) {
  const last = String(name || '').trim().slice(-1);
  const code = last.charCodeAt(0);
  if (!Number.isFinite(code) || code < 0xac00 || code > 0xd7a3) return '를';
  return (code - 0xac00) % 28 ? '을' : '를';
}

function burstLabel(item) {
  const raw = String(item?.burst || item?.burstCode || '').replace(/[^0-9A]/g, '');
  if (raw === '1' || raw === '2' || raw === '3') return `${raw}버`;
  if (raw === 'A') return '올버';
  return '';
}

function targetStatusText(target) {
  if (!target) return '';
  if (target.covered) return target.overkillEok > 0.000001 ? `초과 +${formatRaidHpEok(target.overkillEok)}` : '딱 맞게 처치';
  return `부족 ${formatRaidHpEok(target.shortageEok)}`;
}

// 순위는 1~3단과 4단 보스에 기록된 피해량의 합이다. 1~3단은 남은 체력 위로 때린 만큼이 기록되지
// 않으므로, 여기서 말하는 이득/손해는 원딜이 아니라 기록되는 딜을 기준으로 한다.
function verdictText(result) {
  if (!result) return '';
  const wasteNote = Math.abs(result.wastedDelta) > 0.000001
    ? ` 체력을 넘겨 버려지는 딜은 ${formatEok(Math.abs(result.wastedDelta))} ${result.wastedDelta < 0 ? '줄어듭니다' : '늘어납니다'}.`
    : '';
  switch (result.decidedBy) {
    case 'covered':
      return `이 멤버가 맡은 보스 중 처치 가능한 보스가 ${Math.abs(result.coveredDelta)}개 ${result.coveredDelta > 0 ? '늘어납니다' : '줄어듭니다'}.${wasteNote}`;
    case 'effective':
      return `처치 수는 같고, 순위에 들어가는 기록 딜이 ${formatEok(Math.abs(result.effectiveDelta))} ${result.effectiveDelta > 0 ? '늘어납니다' : '줄어듭니다'}.${wasteNote}`;
    case 'shortage':
      return `처치 수와 기록 딜은 같고, 모자란 체력이 ${formatRaidHpEok(Math.abs(result.shortageDelta))} ${result.shortageDelta < 0 ? '줄어듭니다' : '늘어납니다'}.`;
    case 'overkill':
      return `처치 수와 기록 딜은 같고, 보스를 잡고 남아 버려지는 초과딜이 ${formatRaidHpEok(Math.abs(result.overkillDelta))} ${result.overkillDelta < 0 ? '줄어듭니다' : '늘어납니다'}.`;
    case 'damage':
      return `기록 딜은 같고 원딜만 ${formatEok(Math.abs(result.damageDelta))} ${result.damageDelta > 0 ? '늘어납니다' : '줄어듭니다'}. 늘어난 만큼은 보스 체력을 넘겨 버려집니다.`;
    default:
      return '보스 결과와 기록 딜이 현재 배치와 같습니다.';
  }
}

// Rearranging one member changes what the bosses still need, so the rest of the union should be
// replanned before calling the move a gain. Both arrangements go through the same full-raid
// simulator — the one the recommend dialog uses — in a worker, since a run takes seconds.
function unionVerdictClass(union) {
  const clearedDelta = numberValue(union?.after?.clearedCount) - numberValue(union?.before?.clearedCount);
  const effectiveDelta = numberValue(union?.after?.effectiveEok) - numberValue(union?.before?.effectiveEok);
  if (clearedDelta > 0 || (clearedDelta === 0 && effectiveDelta > 0.000001)) return 'better';
  if (clearedDelta < 0 || effectiveDelta < -0.000001) return 'worse';
  return 'same';
}

function unionVerdictText(union) {
  const clearedDelta = numberValue(union?.after?.clearedCount) - numberValue(union?.before?.clearedCount);
  const effectiveDelta = numberValue(union?.after?.effectiveEok) - numberValue(union?.before?.effectiveEok);
  const hitsDelta = numberValue(union?.after?.spentHits) - numberValue(union?.before?.spentHits);
  // Every attack 1~3단 does not need is an attack that lands on the infinite 4단 boss, where the
  // score is simply the damage dealt — so saving attacks is the win even when 1~3단 reads the same.
  const hitsNote = hitsDelta === 0 ? '' : hitsDelta < 0
    ? ` 남은 멤버가 쓰는 공격이 ${Math.abs(hitsDelta)}회 줄어, 그만큼 4단 보스에 더 쏟을 수 있습니다.`
    : ` 다만 남은 멤버가 공격을 ${hitsDelta}회 더 써야 해서, 4단에 쓸 공격이 그만큼 줄어듭니다.`;
  if (clearedDelta !== 0) {
    return `남은 멤버까지 다시 짜면 처치 가능한 보스가 ${Math.abs(clearedDelta)}개 ${clearedDelta > 0 ? '늘어납니다' : '줄어듭니다'}.${hitsNote}`;
  }
  if (Math.abs(effectiveDelta) > 0.000001) {
    return `처치 수는 같고, 1~3단 기록 딜이 ${formatEok(Math.abs(effectiveDelta))} ${effectiveDelta > 0 ? '늘어납니다' : '줄어듭니다'}.${hitsNote}`;
  }
  if (hitsNote) return `1~3단 결과는 그대로입니다.${hitsNote}`;
  return '남은 멤버까지 다시 짜 보면 유니온 전체 결과는 같습니다.';
}

function useUnionReoptimization({ enabled, bosses, plans, actualRecords, candidates, slots, baseline, selected }) {
  const [state, setState] = useState({ loading: false, error: '', before: null, after: null });
  const workerRef = useRef(null);

  const inputs = useMemo(() => {
    if (!enabled || !baseline || !selected || selected.key === baseline.key) return null;
    const memberCount = new Set((Array.isArray(candidates) ? candidates : [])
      .map((candidate) => String(candidate?.memberOpenid ?? '').trim())
      .filter(Boolean)).size;
    const build = (allocation) => {
      const nextPlans = applyAllocationToPlans({ plans, slots, allocation });
      return {
        input: buildStrategySimulationInput({ bosses, plans: nextPlans, actualRecords, candidates, memberCount }),
        committed: unionCommittedScore({ bosses, plans: nextPlans, actualRecords }),
      };
    };
    return { before: build(baseline), after: build(selected) };
  }, [actualRecords, baseline, bosses, candidates, enabled, plans, selected, slots]);

  // Content key, not object identity: the board rebuilds plans and candidates on every silent raid
  // refresh, and restarting a seconds-long run each time would make the dialog unusable.
  const signature = useMemo(() => (inputs ? JSON.stringify([inputs.before, inputs.after].map((entry) => ({
    targets: entry.input.targets.map((target) => `${target.id}:${target.remaining}`),
    candidates: entry.input.candidates.length,
    usage: [...entry.input.initialUsage.entries()].map(([key, count]) => `${key}:${count}`).sort(),
  }))) : ''), [inputs]);

  useEffect(() => {
    if (!inputs) {
      setState({ loading: false, error: '', before: null, after: null });
      return undefined;
    }
    const worker = new Worker(new URL('../workers/raidFullSimulatorWorker.js', import.meta.url), { type: 'module' });
    workerRef.current = worker;
    let active = true;
    const pending = new Map([['before', null], ['after', null]]);
    setState({ loading: true, error: '', before: null, after: null });

    worker.addEventListener('message', (event) => {
      if (!active) return;
      const requestId = String(event.data?.requestId || '');
      if (!pending.has(requestId)) return;
      if (event.data?.error) {
        setState({ loading: false, error: String(event.data.error), before: null, after: null });
        return;
      }
      pending.set(requestId, unionPlacementScore(inputs[requestId].committed, unionSimulationScore(event.data?.result)));
      if ([...pending.values()].every((value) => value !== null)) {
        setState({ loading: false, error: '', before: pending.get('before'), after: pending.get('after') });
      }
    });
    worker.addEventListener('error', () => {
      if (active) setState({ loading: false, error: '전체 재계산 작업자를 실행하지 못했습니다.', before: null, after: null });
    });
    worker.postMessage({ requestId: 'before', input: inputs.before.input });
    worker.postMessage({ requestId: 'after', input: inputs.after.input });

    return () => {
      active = false;
      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
    };
  }, [signature]);

  useEffect(() => () => {
    workerRef.current?.terminate();
    workerRef.current = null;
  }, []);

  return state;
}

export function RaidStrategyPlacementDialog({
  open = false,
  mobile = false,
  memberOpenid = '',
  bosses = [],
  plans = [],
  actualRecords = [],
  candidates = [],
  focus = null,
  canManage = false,
  onApply = () => '',
  onClose = () => {},
}) {
  const { catalog } = useSharedNikkeCatalog();
  const [selectedKey, setSelectedKey] = useState('');
  const [selectedCode, setSelectedCode] = useState('');
  const [applyNotice, setApplyNotice] = useState('');

  const comparison = useMemo(() => (open && memberOpenid
    ? buildMemberPlacementComparison({ memberOpenid, plans, actualRecords, candidates, focus })
    : null), [actualRecords, candidates, focus, memberOpenid, open, plans]);

  useEffect(() => {
    if (!open || !comparison) return;
    setApplyNotice('');
    const best = comparison.allocations.find((allocation) => allocation.key !== comparison.baseline?.key) || null;
    setSelectedKey(comparison.focusAllocation?.key || best?.key || comparison.baseline?.key || '');
    setSelectedCode(comparison.contested[0]?.code || '');
  }, [comparison, open]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open]);

  // Hooks run before the early return on purpose: this one is only meaningful once the dialog is open,
  // but calling it after the return would change the hook count between closed and open and take the
  // whole page down with it.
  const selectedAllocation = comparison?.allocations.find((allocation) => allocation.key === selectedKey)
    || comparison?.baseline
    || null;
  const union = useUnionReoptimization({
    enabled: open && Boolean(comparison) && Boolean(selectedAllocation)
      && selectedAllocation.key !== comparison.baseline?.key,
    bosses,
    plans,
    actualRecords,
    candidates,
    slots: comparison?.slots || [],
    baseline: comparison?.baseline || null,
    selected: selectedAllocation,
  });

  if (!open || !comparison) return null;

  const { baseline, slots, contested, allocations } = comparison;
  const slotByKey = new Map(slots.map((slot) => [slot.key, slot]));
  const bossName = (bossNo) => bossDisplayName(bosses.find((boss) => Number(boss.bossNo) === Number(bossNo)), bossNo);
  const slotLabel = (slotKey) => {
    const slot = slotByKey.get(slotKey);
    return slot ? `${slot.phaseNo}단 ${bossName(slot.bossNo)}` : '사용 안 함';
  };
  const nikkeName = (code) => findNikkeInCatalog(catalog, code)?.name || code;
  const nikkeLabel = (code) => {
    const item = findNikkeInCatalog(catalog, code);
    const burst = burstLabel(item);
    return burst ? `${item?.name || code} (${burst})` : item?.name || code;
  };

  const selected = allocations.find((allocation) => allocation.key === selectedKey) || baseline;
  const result = selected && baseline ? compareAllocations(baseline, selected) : null;
  const isBaselineSelected = !selected || !baseline || selected.key === baseline.key;
  const selectedRow = contested.find((row) => row.code === selectedCode) || null;

  const applySelected = () => {
    if (!selected || isBaselineSelected) return;
    const error = onApply(selected);
    setApplyNotice(error || '이 배치로 계획표를 바꿨습니다. 저장해야 유니온에 공유됩니다.');
  };

  const dialog = (
    <div className={`raid-strategy-placement-overlay ${mobile ? 'mobile' : 'desktop'}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="raid-strategy-placement-dialog" role="dialog" aria-modal="true" aria-labelledby="raid-strategy-placement-title">
        <header className="raid-strategy-placement-head">
          <div>
            <span>NIKKE PLACEMENT</span>
            <strong id="raid-strategy-placement-title">니케 배치 비교 · {comparison.member.nickname || '멤버'}</strong>
            <small>실전 {comparison.actualAttacks.length}타 · 계획 {slots.length}타</small>
          </div>
          <button type="button" className="ui-icon-action close" onClick={onClose} aria-label="닫기" title="닫기"><DororaidIcon name="close" size={20} /></button>
        </header>

        <div className="raid-strategy-placement-body">
          {applyNotice ? <p className="raid-strategy-placement-notice" role="status">{applyNotice}</p> : null}

          {!slots.length ? (
            <p className="raid-strategy-placement-empty">이 멤버에게 배치된 계획 슬롯이 없습니다.</p>
          ) : (
            <>
              <section className="raid-strategy-placement-current">
                <h4>현재 배치</h4>
                {comparison.actualAttacks.map((attack, index) => (
                  <article className="raid-strategy-placement-slot locked" key={`actual-${index}`}>
                    <div><span>실전 · {attack.phaseNo}단 {bossName(attack.bossNo)}</span><strong>{attack.attackNo ? `${attack.attackNo}타` : '실전 공격'}</strong></div>
                    <RaidTetrisMiniDeck deck={attack.deck} compact />
                    <b>{formatEok(attack.damageEok)}</b>
                    <small>이미 사용한 니케는 다시 쓸 수 없습니다.</small>
                  </article>
                ))}
                {baseline.choices.map((choice) => {
                  const slot = slotByKey.get(choice.slotKey);
                  const target = baseline.targets.find((item) => item.planKey === slot?.planKey) || null;
                  return (
                    <article className="raid-strategy-placement-slot" key={choice.slotKey}>
                      <div><span>계획 · {slotLabel(choice.slotKey)}</span><strong>{choice.option.title}</strong></div>
                      <RaidTetrisMiniDeck deck={choice.option.deck} compact />
                      <b>{formatEok(choice.option.damageEok)}</b>
                      <small className={target?.covered ? 'ready' : 'lack'}>{targetStatusText(target)}</small>
                    </article>
                  );
                })}
              </section>

              {contested.length ? (
                <section className="raid-strategy-placement-contested">
                  <h4>슬롯끼리 겹치는 니케</h4>
                  <p>한 니케는 이 멤버의 공격 중 한 번만 쓸 수 있습니다. 아래 니케를 어디에 두느냐에 따라 나머지 슬롯이 쓸 수 있는 덱이 달라집니다.</p>
                  <div className="raid-strategy-placement-chips" role="tablist" aria-label="겹치는 니케 선택">
                    {contested.map((row) => (
                      <button type="button" role="tab" aria-selected={row.code === selectedCode} className={row.code === selectedCode ? 'active' : ''} key={row.code} onClick={() => setSelectedCode(row.code)}>
                        {nikkeLabel(row.code)}
                        {row.spreadEok > 0.000001 ? <small>최대 {formatEok(row.spreadEok)} 차이</small> : null}
                      </button>
                    ))}
                  </div>

                  {selectedRow ? (
                    <div className="raid-strategy-placement-table-wrap">
                      <table className="raid-strategy-placement-table">
                        <caption>{nikkeName(selectedRow.code)}{objectParticle(nikkeName(selectedRow.code))} 어디에 쓸까?</caption>
                        <thead>
                          <tr><th scope="col">배치</th><th scope="col">기록 딜</th><th scope="col">차이</th><th scope="col">보스 결과</th></tr>
                        </thead>
                        <tbody>
                          {selectedRow.placements.map((placement) => {
                            const allocation = placement.allocation;
                            const current = placement.slotKey === selectedRow.currentSlotKey;
                            const damageDelta = allocation ? allocation.memberEffectiveEok - baseline.memberEffectiveEok : 0;
                            return (
                              <tr key={placement.slotKey || 'none'} className={`${allocation ? '' : 'blocked'} ${allocation && allocation.key === selected?.key ? 'selected' : ''}`.trim()}>
                                <th scope="row">
                                  {allocation ? (
                                    <button type="button" onClick={() => setSelectedKey(allocation.key)}>{slotLabel(placement.slotKey)}{current ? ' (현재)' : ''}</button>
                                  ) : <span>{slotLabel(placement.slotKey)}</span>}
                                </th>
                                {allocation ? <>
                                  <td title={`원딜 ${formatEok(allocation.memberTotalEok)} 중 체력을 넘겨 버려지는 딜 ${formatEok(allocation.wastedEok)}`}>
                                    {formatEok(allocation.memberEffectiveEok)}
                                    {allocation.wastedEok > 0.000001 ? <em className="worse">버림 {formatEok(allocation.wastedEok)}</em> : null}
                                  </td>
                                  <td className={damageDelta > 0 ? 'better' : damageDelta < 0 ? 'worse' : 'same'}>{current ? '기준' : signedEok(damageDelta)}</td>
                                  <td>처치 {allocation.coveredCount}/{allocation.targets.length}{allocation.shortageSumEok > 0.000001 ? ` · 부족 ${formatRaidHpEok(allocation.shortageSumEok)}` : ''}</td>
                                </> : (
                                  <td colSpan={3} className="blocked">
                                    {placement.blockedBy.length
                                      ? `${placement.blockedBy.map(slotLabel).join(', ')}에 ${nikkeName(selectedRow.code)} 없이 측정한 모의전이 없어 이 배치를 만들 수 없습니다.`
                                      : '다른 니케까지 겹쳐서 이 배치를 만들 수 있는 조합이 없습니다.'}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </section>
              ) : (
                <p className="raid-strategy-placement-empty">이 멤버의 슬롯끼리 겹치는 니케가 없습니다. 지금 배치에서는 니케를 옮겨 얻을 이득이 없습니다.</p>
              )}

              {result && !isBaselineSelected ? (
                <section className="raid-strategy-placement-compare">
                  <div className={`raid-strategy-placement-verdict ${result.verdict}`}>
                    <strong>{result.verdict === 'gain' ? '바꾸면 이득' : result.verdict === 'loss' ? '바꾸면 손해' : '결과 동일'}</strong>
                    <p>{verdictText(result)}</p>
                  </div>

                  <div className="raid-strategy-placement-table-wrap">
                    <table className="raid-strategy-placement-table compare">
                      <thead>
                        <tr><th scope="col">슬롯</th><th scope="col">현재</th><th scope="col">바꾼 뒤</th><th scope="col">보스 결과</th></tr>
                      </thead>
                      <tbody>
                        {result.slots.map((slot) => (
                          <tr key={slot.slotKey} className={slot.changed ? 'changed' : ''}>
                            <th scope="row">{slotLabel(slot.slotKey)}</th>
                            <td><span>{slot.before?.title}</span><b>{formatEok(slot.before?.damageEok)}</b><RaidTetrisMiniDeck deck={slot.before?.deck || []} compact /></td>
                            <td><span>{slot.after.title}</span><b>{formatEok(slot.after.damageEok)} <em className={slot.damageDelta > 0 ? 'better' : slot.damageDelta < 0 ? 'worse' : 'same'}>{signedEok(slot.damageDelta)}</em></b><RaidTetrisMiniDeck deck={slot.after.deck} compact /></td>
                            <td className="raid-strategy-placement-result">
                              <span>{targetStatusText(slot.targetBefore)}</span>
                              <b className={slot.targetAfter?.covered ? 'ready' : 'lack'}>→ {targetStatusText(slot.targetAfter)}</b>
                            </td>
                          </tr>
                        ))}
                        <tr className="total">
                          <th scope="row">기록 딜 합계</th>
                          <td>{formatEok(baseline.memberEffectiveEok)}</td>
                          <td>{formatEok(selected.memberEffectiveEok)} <em className={result.effectiveDelta > 0 ? 'better' : result.effectiveDelta < 0 ? 'worse' : 'same'}>{signedEok(result.effectiveDelta)}</em></td>
                          <td className="raid-strategy-placement-result">
                            <span>처치 {baseline.coveredCount} / {selected.targets.length}</span>
                            <b>→ {selected.coveredCount} / {selected.targets.length}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <section className="raid-strategy-placement-union">
                    <h4>유니온 전체 재계산</h4>
                    <p>이 멤버가 배치를 바꾸면 보스에 남는 체력이 달라져서, 나머지 멤버가 어디를 때려야 하는지도 같이 바뀝니다. 아래는 두 경우 각각에 대해 남은 멤버 배치를 전체 시뮬레이터로 다시 짜 본 결과입니다. 1~3단에서 덜 쓴 공격은 체력이 무한인 4단 보스로 갑니다.</p>
                    {union.loading ? <p className="raid-strategy-placement-union-state" role="status">남은 멤버 배치를 다시 짜는 중…</p> : null}
                    {union.error ? <p className="raid-strategy-placement-union-state error" role="status">{union.error}</p> : null}
                    {!union.loading && !union.error && union.before && union.after ? (
                      <div className="raid-strategy-placement-table-wrap">
                        <table className="raid-strategy-placement-table">
                          <thead>
                            <tr><th scope="col">유니온</th><th scope="col">처치</th><th scope="col">기록 딜</th><th scope="col">남은 멤버 공격</th></tr>
                          </thead>
                          <tbody>
                            <tr><th scope="row">현재 배치 기준</th><td>{union.before.clearedCount}</td><td>{formatEok(union.before.effectiveEok)}</td><td>{union.before.spentHits}회</td></tr>
                            <tr className="selected">
                              <th scope="row">바꾼 배치 기준</th>
                              <td>{union.after.clearedCount}</td>
                              <td>
                                {formatEok(union.after.effectiveEok)}
                                {Math.abs(union.after.effectiveEok - union.before.effectiveEok) > 0.000001 ? (
                                  <em className={unionVerdictClass(union)}>{signedEok(union.after.effectiveEok - union.before.effectiveEok)}</em>
                                ) : null}
                              </td>
                              <td className={union.after.spentHits < union.before.spentHits ? 'better' : union.after.spentHits > union.before.spentHits ? 'worse' : 'same'}>
                                {union.after.spentHits}회
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                    {!union.loading && !union.error && union.before && union.after ? (
                      <p className="raid-strategy-placement-union-note">{unionVerdictText(union)}</p>
                    ) : null}
                  </section>

                  {result.moved.length ? (
                    <ul className="raid-strategy-placement-moved">
                      {result.moved.map((entry) => (
                        <li key={`${entry.code}-${entry.from}-${entry.to}`}>{nikkeLabel(entry.code)}: {entry.from ? slotLabel(entry.from) : '사용 안 함'} → {entry.to ? slotLabel(entry.to) : '사용 안 함'}</li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              ) : (
                <p className="raid-strategy-placement-empty">현재 배치가 선택돼 있습니다. 위 표에서 다른 배치를 고르면 비교가 나옵니다.</p>
              )}

              {allocations.length > 1 ? (
                <section className="raid-strategy-placement-alternatives">
                  <h4>다른 배치 조합</h4>
                  <ol>
                    {allocations.slice(0, 5).map((allocation) => {
                      const damageDelta = allocation.memberEffectiveEok - baseline.memberEffectiveEok;
                      const isCurrent = allocation.key === baseline.key;
                      // Listing every slot twice buries the one deck that actually moves. Name only the
                      // slots that differ from the current arrangement, as 바꾸기 전 → 바꾼 뒤.
                      const changes = allocation.choices
                        .map((choice) => ({ choice, before: baseline.optionBySlot.get(choice.slotKey) || null }))
                        .filter((entry) => entry.before?.id !== entry.choice.option.id);
                      return (
                        <li key={allocation.key}>
                          <button type="button" className={allocation.key === selected?.key ? 'active' : ''} onClick={() => setSelectedKey(allocation.key)}>
                            <span className="raid-strategy-placement-alt-changes">
                              {isCurrent || !changes.length ? <em>현재 배치 그대로</em> : changes.map((entry) => (
                                <span key={entry.choice.slotKey}>
                                  <strong>{slotLabel(entry.choice.slotKey)}</strong>
                                  <i>{entry.before?.title || '없음'}</i>
                                  <u>→ {entry.choice.option.title}</u>
                                </span>
                              ))}
                            </span>
                            <b>처치 {allocation.coveredCount}/{allocation.targets.length} · {formatEok(allocation.memberEffectiveEok)} <em className={damageDelta > 0 ? 'better' : damageDelta < 0 ? 'worse' : 'same'}>{isCurrent ? '현재' : signedEok(damageDelta)}</em></b>
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </section>
              ) : null}
            </>
          )}
        </div>

        <footer className="raid-strategy-placement-actions">
          <button type="button" onClick={onClose}>닫기</button>
          {canManage ? <button type="button" className="primary" disabled={isBaselineSelected} onClick={applySelected}>이 배치로 적용</button> : null}
        </footer>
      </section>
    </div>
  );

  return createPortal(dialog, document.body);
}
